import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Col, DatePicker, Form, Input, InputNumber, message, Radio, Row,
  Select, Space, Table, Typography,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import {
  LegalEntity, LEGAL_ENTITY_LABELS, ReceiptItemIn, createReceipt,
  StockItem, listStockItems,
} from '../api/stock';

const { Title } = Typography;

interface DraftLine {
  key: string;
  // reference to existing stock_item OR new item
  stock_item_id: number | null;
  name: string;
  unit: string;
  is_marked: boolean;
  quantity: number;
  price: number | null;
}

const newLine = (): DraftLine => ({
  key: Math.random().toString(36).slice(2),
  stock_item_id: null, name: '', unit: 'шт', is_marked: false, quantity: 1, price: null,
});

const StockReceiptNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [legalEntity, setLegalEntity] = useState<LegalEntity>('autoregium');
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [saving, setSaving] = useState(false);

  // Suppliers
  const [supplierOptions, setSupplierOptions] = useState<{ value: number; label: string }[]>([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const loadSuppliers = useCallback(async (q?: string) => {
    setSupplierLoading(true);
    try {
      const r = await api.get('/counterparties', { params: { search: q, per_page: 30 } });
      setSupplierOptions((r.data.items || []).map((c: any) => ({ value: c.id, label: c.short_name })));
    } finally {
      setSupplierLoading(false);
    }
  }, []);
  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  // Existing stock items (for auto-complete)
  const [stockOptions, setStockOptions] = useState<StockItem[]>([]);
  const [stockLoading, setStockLoading] = useState(false);
  const loadStockOptions = useCallback(async (q?: string) => {
    setStockLoading(true);
    try {
      const data = await listStockItems({ legal_entity: legalEntity, search: q, per_page: 30 });
      setStockOptions(data.items);
    } finally {
      setStockLoading(false);
    }
  }, [legalEntity]);
  useEffect(() => { loadStockOptions(); }, [loadStockOptions]);

  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const pickExistingItem = (key: string, itemId: number) => {
    const s = stockOptions.find((x) => x.id === itemId);
    if (!s) return;
    updateLine(key, {
      stock_item_id: s.id,
      name: s.name,
      unit: s.unit,
      is_marked: s.is_marked,
    });
  };

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
  };

  const addLine = () => setLines((prev) => [...prev, newLine()]);

  const handleSubmit = async () => {
    // Валидация
    const payloadLines: ReceiptItemIn[] = [];
    for (const l of lines) {
      if (!l.stock_item_id && !l.name?.trim()) {
        message.error('Укажите название товара или выберите из склада');
        return;
      }
      if (!l.quantity || l.quantity <= 0) {
        message.error('Количество должно быть > 0');
        return;
      }
      payloadLines.push({
        stock_item_id: l.stock_item_id,
        name: l.stock_item_id ? undefined : l.name.trim(),
        unit: l.stock_item_id ? undefined : l.unit,
        is_marked: l.stock_item_id ? undefined : l.is_marked,
        quantity: l.quantity,
        price: l.price ?? null,
      });
    }
    if (!payloadLines.length) {
      message.error('Добавьте хотя бы одну позицию');
      return;
    }

    setSaving(true);
    try {
      const res = await createReceipt({
        legal_entity: legalEntity,
        supplier_id: supplierId,
        date: date.format('YYYY-MM-DD'),
        invoice_number: invoiceNumber || null,
        notes: notes || null,
        items: payloadLines,
      });
      message.success(`Приход #${res.id} сохранён`);
      navigate('/stock/receipts');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const total = lines.reduce(
    (s, l) => s + (l.quantity || 0) * (l.price || 0),
    0,
  );

  return (
    <Card>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Назад</Button>
        <Title level={4} style={{ margin: 0 }}>Новый приход</Title>
      </Space>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Form.Item label="Юр. лицо" required style={{ marginBottom: 0 }}>
            <Radio.Group value={legalEntity} onChange={(e) => setLegalEntity(e.target.value)}>
              <Radio.Button value="autoregium">АвтоРегиум</Radio.Button>
              <Radio.Button value="fomin">Фомин</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Поставщик" style={{ marginBottom: 0 }}>
            <Select
              allowClear
              showSearch
              filterOption={false}
              placeholder="Контрагент (необязательно)"
              loading={supplierLoading}
              onSearch={loadSuppliers}
              value={supplierId ?? undefined}
              onChange={(v) => setSupplierId(v ?? null)}
              options={supplierOptions}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={4}>
          <Form.Item label="Дата" style={{ marginBottom: 0 }}>
            <DatePicker value={date} onChange={(v) => v && setDate(v)} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={12} md={4}>
          <Form.Item label="Накладная №" style={{ marginBottom: 0 }}>
            <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </Form.Item>
        </Col>
      </Row>

      <Table
        size="small"
        rowKey="key"
        dataSource={lines}
        pagination={false}
        style={{ marginBottom: 16 }}
        columns={[
          {
            title: 'Позиция (существующая или новая)', key: 'item',
            render: (_: unknown, l: DraftLine) => (
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  showSearch
                  allowClear
                  filterOption={false}
                  placeholder="Выбрать из склада"
                  style={{ width: 240 }}
                  value={l.stock_item_id ?? undefined}
                  onSearch={loadStockOptions}
                  loading={stockLoading}
                  onChange={(v) => {
                    if (v) pickExistingItem(l.key, v);
                    else updateLine(l.key, { stock_item_id: null });
                  }}
                  options={stockOptions.map((s) => ({
                    value: s.id,
                    label: `${s.name} · остаток ${Number(s.current_stock).toLocaleString('ru-RU')} ${s.unit}`,
                  }))}
                />
                <Input
                  placeholder="Либо новое название"
                  disabled={l.stock_item_id != null}
                  value={l.name}
                  onChange={(e) => updateLine(l.key, { name: e.target.value })}
                />
              </Space.Compact>
            ),
          },
          {
            title: 'Ед.', dataIndex: 'unit', key: 'unit', width: 90,
            render: (v: string, l: DraftLine) => (
              <Input
                disabled={l.stock_item_id != null}
                value={v}
                onChange={(e) => updateLine(l.key, { unit: e.target.value })}
              />
            ),
          },
          {
            title: 'Кол-во', dataIndex: 'quantity', key: 'quantity', width: 110,
            render: (v: number, l: DraftLine) => (
              <InputNumber
                min={0.001}
                step={1}
                value={v}
                onChange={(val) => updateLine(l.key, { quantity: Number(val) })}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Цена, ₽', dataIndex: 'price', key: 'price', width: 120,
            render: (v: number | null, l: DraftLine) => (
              <InputNumber
                min={0}
                step={50}
                value={v ?? undefined}
                onChange={(val) => updateLine(l.key, { price: val == null ? null : Number(val) })}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Сумма', key: 'sum', width: 110, align: 'right' as const,
            render: (_: unknown, l: DraftLine) => {
              if (l.price == null) return <span style={{ color: '#999' }}>—</span>;
              return `${((l.quantity || 0) * l.price).toLocaleString('ru-RU')} ₽`;
            },
          },
          {
            title: '', key: 'actions', width: 50,
            render: (_: unknown, l: DraftLine) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={lines.length === 1}
                onClick={() => removeLine(l.key)}
              />
            ),
          },
        ]}
      />

      <Space style={{ marginBottom: 16 }}>
        <Button icon={<PlusOutlined />} onClick={addLine}>Добавить строку</Button>
      </Space>

      <Row gutter={16} align="middle" justify="space-between">
        <Col flex={1}>
          <Input.TextArea
            placeholder="Примечание (необязательно)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </Col>
        <Col>
          <Space direction="vertical" align="end">
            <Typography.Text strong>
              Итого: {total.toLocaleString('ru-RU')} ₽
            </Typography.Text>
            <Typography.Text type="secondary">
              Юр. лицо: {LEGAL_ENTITY_LABELS[legalEntity]}
            </Typography.Text>
          </Space>
        </Col>
      </Row>

      <Row justify="end" style={{ marginTop: 16 }}>
        <Space>
          <Button onClick={() => navigate(-1)}>Отмена</Button>
          <Button type="primary" onClick={handleSubmit} loading={saving}>Сохранить приход</Button>
        </Space>
      </Row>
    </Card>
  );
};

export default StockReceiptNewPage;
